import {
  Badge,
  Box,
  Button,
  Flex,
  Heading,
  HStack,
  Icon,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Stack,
  Text,
  useDisclosure,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { useGetUserVerificationStatus } from 'domains/user/hooks/useGetUserVerificationStatus';
import { useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import DocumentUploader from 'components/documents/DocumentUploader';
import DocumentList from 'components/documents/DocumentList';
import { useFirebaseAuth } from 'domains/auth/hooks/useFirebaseAuth';
import { getAuth, signOut } from 'firebase/auth';
import { useTranslation } from 'i18n/TranslationProvider';
import heroIllustration from 'assets/img/auth/auth_verification_hero.png';
import LanguageSelector from 'components/i18n/LanguageSelector';
import { UnifiedAuthPanel } from 'components/layout/UnifiedPrimitives';
import {
  FiArrowLeft,
  FiFileText,
  FiRefreshCw,
  FiShield,
  FiUploadCloud,
} from 'react-icons/fi';

const VerificationPendingScreen = () => {
  const { t } = useTranslation();
  const {
    verificationStatus,
    loading: verificationLoading,
    getUserVerificationStatus,
  } = useGetUserVerificationStatus();
  const { uid, loading: authLoading } = useFirebaseAuth();
  const navigate = useNavigate();
  const [triggerRefresh, setTriggerRefresh] = useState(0);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const uploadModal = useDisclosure();

  const pageBg = useColorModeValue(
    'linear(to-br, #f8fbff, #eef4ff 35%, #e9f0ff)',
    'linear(to-br, #0f1835, #161f45 45%, #1d2a56)',
  );
  const titleColor = useColorModeValue('navy.700', 'white');
  const textMuted = useColorModeValue('gray.600', 'gray.300');
  const iconBg = useColorModeValue('orange.50', 'rgba(251,146,60,0.12)');
  const iconColor = useColorModeValue('orange.500', 'orange.300');
  const uploadIconBg = useColorModeValue('blue.50', 'rgba(57,101,255,0.12)');
  const uploadIconColor = useColorModeValue('blue.500', 'blue.300');
  const statusBadgeScheme = verificationStatus === 'approved' ? 'green' : 'orange';
  const panelBorder = useColorModeValue('rgba(255,255,255,0.9)', 'rgba(255,255,255,0.15)');
  const infoBg = useColorModeValue('rgba(251,146,60,0.06)', 'rgba(251,146,60,0.10)');
  const infoBorder = useColorModeValue('rgba(251,146,60,0.18)', 'rgba(251,146,60,0.22)');
  const dividerColor = useColorModeValue('gray.100', 'whiteAlpha.100');

  useEffect(() => {
    if (!authLoading && !uid) {
      navigate('/auth/sign-in');
      return;
    }
    if (verificationStatus === 'approved') {
      navigate('/');
    }
  }, [uid, authLoading, verificationStatus, navigate]);

  const handleGoToSignIn = async () => {
    setIsSigningOut(true);
    try {
      const auth = getAuth();
      await signOut(auth);
    } catch (error) {
      console.error('Error signing out:', error);
    } finally {
      setIsSigningOut(false);
    }
    navigate('/auth/sign-in');
  };

  return (
    <Box position="relative" minH="100dvh" bgGradient={pageBg} overflow="hidden">
      {/* Background orbs */}
      <Box
        position="absolute"
        top="-220px"
        right="-220px"
        w="560px"
        h="560px"
        borderRadius="full"
        bgGradient="radial(brand.300, transparent 68%)"
        opacity={0.35}
        pointerEvents="none"
      />
      <Box
        position="absolute"
        bottom="-260px"
        left="-180px"
        w="520px"
        h="520px"
        borderRadius="full"
        bgGradient="radial(orange.300, transparent 70%)"
        opacity={0.18}
        pointerEvents="none"
      />

      {/* Language selector */}
      <Flex position="absolute" top={{ base: 4, md: 8 }} right={{ base: 4, md: 8 }} zIndex={2}>
        <LanguageSelector size="sm" variant="solid" showLabel />
      </Flex>

      {/* Main layout */}
      <Flex
        position="relative"
        minH="100dvh"
        w="100%"
        px={{ base: 4, sm: 6, md: 8 }}
        pt={{ base: 'calc(env(safe-area-inset-top) + 0.75rem)', md: 6 }}
        pb={{ base: 'calc(env(safe-area-inset-bottom) + 0.75rem)', md: 6 }}
        align="center"
        justify="center"
      >
        <Flex
          w="100%"
          maxW={{ base: '100%', sm: '560px', md: '700px', xl: '1080px', '2xl': '1180px' }}
          justify={{ base: 'center', xl: 'space-between' }}
          align="stretch"
          gap={{ xl: 5, '2xl': 8 }}
        >
          {/* Content side */}
          <Box
            flex={1}
            w="100%"
            maxW={{ base: '100%', sm: '560px', md: '700px' }}
            mx="auto"
            zIndex={1}
            display="flex"
            flexDirection="column"
            justifyContent="center"
            py={{ base: 4, md: 0 }}
          >
            <Stack spacing={3} w="100%" maxW={{ base: '100%', sm: '560px', md: '700px' }} mx="auto">
              {/* ── Header card ── */}
              <UnifiedAuthPanel px={{ base: 4, sm: 6, md: 7 }} py={{ base: 4, md: 5 }}>
                <Stack spacing={3}>
                  {/* Status badges row */}
                  <HStack justify="space-between" align="center" flexWrap="wrap" spacing={2}>
                    <Badge
                      colorScheme="orange"
                      borderRadius="full"
                      px={3}
                      py={1}
                      fontSize="xs"
                      textTransform="uppercase"
                      letterSpacing="wider"
                    >
                      {t('auth.account_status_pending_review')}
                    </Badge>
                    <Badge
                      colorScheme={statusBadgeScheme}
                      borderRadius="full"
                      px={3}
                      py={1}
                      fontSize="xs"
                      textTransform="uppercase"
                    >
                      {verificationStatus ? t(`validationStatus_${verificationStatus.toLowerCase()}`) : t('auth.pending')}
                    </Badge>
                  </HStack>

                  {/* Icon + Heading */}
                  <HStack spacing={4} align="center">
                    <Box
                      w={14}
                      h={14}
                      borderRadius="16px"
                      bg={iconBg}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      flexShrink={0}
                      boxShadow="0 4px 16px rgba(251,146,60,0.20)"
                    >
                      <Icon as={FiShield} color={iconColor} boxSize={7} />
                    </Box>
                    <Heading
                      color={titleColor}
                      fontSize={{ base: 'xl', md: '2xl' }}
                      lineHeight="1.15"
                    >
                      {t('auth.your_account_is_pending_approval')}
                    </Heading>
                  </HStack>

                  {/* Status pulse row */}
                  <HStack
                    spacing={2}
                    align="center"
                    px={3}
                    py={2}
                    borderRadius="10px"
                    bg={infoBg}
                    border="1px solid"
                    borderColor={infoBorder}
                  >
                    <Box
                      w="8px"
                      h="8px"
                      borderRadius="full"
                      bg="orange.400"
                      flexShrink={0}
                      style={{ animation: 'pulse 2s cubic-bezier(0.4,0,0.6,1) infinite' }}
                    />
                    <Text color={textMuted} fontSize="sm" fontWeight="500" noOfLines={{ base: 3, md: 2 }}>
                      {t(
                        'auth.upload_your_verification_documents_below_and_we_ll_notify_you_by_email_once_your_account_is_approved',
                      )}
                    </Text>
                    <style>{`@keyframes pulse { 0%,100%{opacity:1} 50%{opacity:0.45} }`}</style>
                  </HStack>

                  {/* Check status button */}
                  <HStack spacing={3}>
                    <Button
                      leftIcon={<FiRefreshCw />}
                      size="sm"
                      variant="outline"
                      onClick={() => getUserVerificationStatus()}
                      isLoading={verificationLoading}
                      h="38px"
                      fontWeight="600"
                    >
                      {t('auth.check_status')}
                    </Button>
                    {verificationLoading && <Spinner size="sm" color="orange.400" />}
                  </HStack>
                </Stack>
              </UnifiedAuthPanel>

              {/* ── Documents card ── */}
              <UnifiedAuthPanel
                borderRadius={{ base: '20px', md: '22px' }}
                px={{ base: 4, sm: 5, md: 6 }}
                py={{ base: 4, md: 5 }}
                boxShadow="0 16px 40px rgba(9, 30, 66, 0.10)"
              >
                <Stack spacing={3}>
                  <Flex justify="space-between" align={{ base: 'flex-start', md: 'center' }} gap={3} wrap="wrap">
                    <HStack spacing={3} align="center">
                      <Box
                        w={10}
                        h={10}
                        borderRadius="12px"
                        bg={uploadIconBg}
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                        flexShrink={0}
                      >
                        <Icon as={FiFileText} color={uploadIconColor} boxSize={5} />
                      </Box>
                      <Box>
                        <Text fontWeight="700" color={titleColor} fontSize={{ base: 'md', md: 'lg' }}>
                          {t('auth.your_uploaded_documents')}
                        </Text>
                        <Text color={textMuted} fontSize="sm">
                          {t('auth.after_uploading_support_staff_can_review_and_approve_your_account')}
                        </Text>
                      </Box>
                    </HStack>

                    <Button
                      leftIcon={<FiUploadCloud />}
                      colorScheme="blue"
                      onClick={uploadModal.onOpen}
                      h="38px"
                      px={4}
                    >
                      {t('auth.upload_verification_documents')}
                    </Button>
                  </Flex>

                  <Box borderTop="1px solid" borderColor={dividerColor} />

                  <DocumentList
                    entityType="user"
                    entityId={uid}
                    docType="verification"
                    refreshTrigger={triggerRefresh}
                    canDelete
                    resolveRole={false}
                    currentRole="partner"
                  />
                </Stack>
              </UnifiedAuthPanel>

              {/* ── Back to sign in ── */}
              <Flex justify="flex-end">
                <Button
                  leftIcon={<FiArrowLeft />}
                  colorScheme="red"
                  variant="solid"
                  onClick={handleGoToSignIn}
                  isLoading={isSigningOut}
                  h="44px"
                  px={6}
                  fontWeight="700"
                  fontSize="sm"
                >
                  {t('auth.back_to_sign_in')}
                </Button>
              </Flex>
            </Stack>
          </Box>

          {/* ── Illustration side ── */}
          <Box
            flex={1}
            display={{ base: 'none', xl: 'flex' }}
            w="100%"
            maxW={{ base: '100%', sm: '480px', md: '520px' }}
            mx="auto"
            opacity={0.95}
            pointerEvents="none"
          >
            <Flex
              position="relative"
              w="100%"
              h="100%"
              minH="420px"
              overflow="hidden"
              borderRadius={{ base: '22px', md: '28px' }}
              border="1px solid"
              borderColor={panelBorder}
              bg="#142B76"
              boxShadow="0 24px 70px rgba(11, 31, 77, 0.20)"
              align="center"
              justify="center"
              flexDirection="column"
            >
              <Box
                position="absolute"
                inset={0}
                bgImage={`url(${heroIllustration})`}
                bgSize="cover"
                bgPosition="center"
                bgRepeat="no-repeat"
              />
              <Box
                position="absolute"
                inset={0}
                bgGradient="linear(to-b, rgba(8,24,66,0.02), rgba(8,24,66,0.55))"
              />
              <VStack
                position="relative"
                zIndex={1}
                spacing={3}
                px={8}
                pb={10}
                mt="auto"
              >
                <Badge
                  borderRadius="full"
                  px={4}
                  py={1.5}
                  fontSize="xs"
                  textTransform="uppercase"
                  letterSpacing="widest"
                  bg="rgba(251,146,60,0.22)"
                  color="orange.200"
                  border="1px solid rgba(251,146,60,0.30)"
                >
                  {t('auth.account_verification_badge')}
                </Badge>
                <Heading
                  color="white"
                  fontSize="2xl"
                  textAlign="center"
                  lineHeight="1.2"
                  fontWeight="800"
                  textShadow="0 2px 20px rgba(0,0,0,0.4)"
                >
                  {t('auth.documents_under_review_heading')}
                </Heading>
                <Text
                  color="whiteAlpha.700"
                  fontSize="sm"
                  textAlign="center"
                  maxW="36ch"
                >
                  {t('auth.documents_under_review_subline')}
                </Text>
              </VStack>
            </Flex>
          </Box>
        </Flex>
      </Flex>

      <Modal isOpen={uploadModal.isOpen} onClose={uploadModal.onClose} size="xl" isCentered>
        <ModalOverlay />
        <ModalContent borderRadius="18px">
          <ModalHeader>{t('auth.upload_verification_documents')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody pb={6}>
            <Stack spacing={3}>
              <Text fontSize="sm" color={textMuted}>
                {t('auth.after_uploading_support_staff_can_review_and_approve_your_account')}
              </Text>
              <DocumentUploader
                entityType="user"
                entityId={uid}
                docType="verification"
                onUploaded={() => {
                  setTriggerRefresh((prev) => prev + 1);
                  uploadModal.onClose();
                }}
              />
            </Stack>
          </ModalBody>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default VerificationPendingScreen;
